import { useRef, useEffect } from "react";
import NextLink from "next/link";
import {
  ErrorText,
  Text,
  Link,
  Heading,
  Loader,
} from "@cruk/cruk-react-components";

import { formatMoneyWithCommas } from "@fwa/src/utils/formatUtils";
import { memberPageIconUrlForTheme } from "@fwa/src/utils/memberUtils";
import { useFundraiserContext } from "@fwa/src/contexts/FundraiserContext";
import { useBrandContext } from "@fwa/src/contexts/BrandContext";
import { imagePathFromImageType } from "@fwa/src/services/imageService";

import sharedStyles from "@fwa/src/components/styles.module.css";
import styles from "@fwa/src/components/LeaderboardList/styles.module.css";

import { type TeamPageType, type FundraisingPageType } from "@fwa/src/types";

const CrukLogoTransparent = "/assets/images/logos/c-transparent-128.png";

// TeamLeaderboardListPaginated handles page data and pagination state
// TeamLeaderboardList handles membership data for current page
export const LeaderboardList = ({
  error,
  pages,
  memberType = "page",
  currentPage,
  itemsPerPage = 10,
}: {
  error: Error | null;
  pages: FundraisingPageType[] | TeamPageType[] | null;
  memberType: "page" | "team";
  currentPage: number;
  itemsPerPage?: number;
}) => {
  const [fundraiserContext] = useFundraiserContext();
  const { fundraiser } = fundraiserContext;
  const usersPositionRef = useRef<HTMLDivElement>(null);
  const [brandName] = useBrandContext();
  const iconUrl = memberPageIconUrlForTheme(brandName);

  // Find the logged-in user's position and scroll to it after render
  useEffect(() => {
    if (pages && fundraiser) {
      const userPageIndex = pages.findIndex((page) => {
        if (memberType === "team") {
          const teamPage = page as TeamPageType;
          return (
            teamPage?.teamLeaderPage?.fundraiser &&
            fundraiser.uniqueId === teamPage.teamLeaderPage.fundraiser.uniqueId
          );
        } else {
          const fundraisingPage = page as FundraisingPageType;
          return (
            fundraisingPage?.fundraiser &&
            fundraiser.uniqueId === fundraisingPage.fundraiser.uniqueId
          );
        }
      });

      if (userPageIndex !== -1 && usersPositionRef.current) {
        usersPositionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [pages, fundraiser, memberType]);

  return (
    <>
      {/* Loading state */}
      {!error && !pages ? <Loader /> : null}
      {/* Error state */}
      {error ? <ErrorText>Unable to get members</ErrorText> : null}
      {/* Empty state */}
      {!error && pages && !pages.length ? (
        <Text>This team currently has no members</Text>
      ) : null}
      {/* Data state */}
      {!error && pages?.length ? (
        <ol
          className={styles["leader-board-ordered-list"]}
          data-component="leaderboard-list"
        >
          {pages.map(
            (page: FundraisingPageType | TeamPageType, index: number) => {
              const positionOffset =
                Math.max(currentPage - 1, 0) * itemsPerPage;

              const medal =
                currentPage === 1 && index === 0
                  ? "gold"
                  : currentPage === 1 && index === 1
                    ? "silver"
                    : currentPage === 1 && index === 2
                      ? "bronze"
                      : null;
              const raisedAmount = page?.donationsTotalAmount
                ? formatMoneyWithCommas(page?.donationsTotalAmount)
                : "0.00";

              const profileImageUrl =
                memberType === "team"
                  ? ""
                  : page.fundraiser?.profileImage?.entityType ===
                      "ImageFacebook"
                    ? `${page.fundraiser?.profileImage.url}?width=300&height=300`
                    : page.fundraiser?.profileImage
                      ? imagePathFromImageType(page.fundraiser?.profileImage)
                      : "";

              const fundraisingPage = page as FundraisingPageType;
              const teamPage = page as TeamPageType;

              const isLoggedInUser =
                memberType === "team"
                  ? fundraiser &&
                    teamPage?.teamLeaderPage?.fundraiser &&
                    fundraiser.uniqueId ===
                      teamPage.teamLeaderPage.fundraiser.uniqueId
                  : fundraiser &&
                    fundraisingPage &&
                    fundraiser.uniqueId === page.fundraiser.uniqueId;

              return (
                <li key={page.uniqueId}>
                  <NextLink
                    href={`/${memberType}${page.url.slice(
                      page.url.lastIndexOf("/"),
                    )}`}
                    data-cta-type="link-member-page"
                  >
                    <div
                      className={[
                        styles["leaderboard-row"],
                        "spacing-props",
                        "color-props",
                      ].join(" ")}
                      data-padding-vertical="xs"
                      data-padding-horizontal="s"
                      data-bg-color={
                        isLoggedInUser
                          ? "background-light-color"
                          : "background-light"
                      }
                      ref={isLoggedInUser ? usersPositionRef : null}
                    >
                      <div
                        className={[
                          styles["position-wrapper"],
                          "spacing-props",
                        ].join(" ")}
                        data-margin-right="xs"
                        data-margin-bottom="none"
                      >
                        <div
                          className={styles["column-space-around-full-height"]}
                        >
                          <Heading
                            h2
                            textSize="xl"
                            as="span"
                            margin="none"
                          >{`${index + positionOffset + 1}`}</Heading>
                        </div>
                      </div>
                      <div className={styles["image-and-text-wrapper"]}>
                        <div
                          className={[
                            styles["icon-wrapper"],
                            "spacing-props",
                          ].join(" ")}
                          data-margin-bottom="none"
                        >
                          <div
                            className={styles["profile-image-wrapper"]}
                            style={{
                              borderColor:
                                medal === "gold"
                                  ? "#FFCA00"
                                  : medal === "silver"
                                    ? "#B4B4B4"
                                    : medal === "bronze"
                                      ? "#CC8B38"
                                      : "var(--clr-avatar-border, #000)",
                            }}
                          >
                            {profileImageUrl.length ? (
                              <img
                                className={styles["profile-image"]}
                                src={profileImageUrl}
                                alt=""
                              />
                            ) : (
                              <img src={iconUrl} alt="" />
                            )}
                          </div>
                          {medal ? (
                            <div
                              className={styles["medal-icon-wrapper"]}
                              style={{
                                background: `${
                                  medal === "gold"
                                    ? "transparent radial-gradient(closest-side at 78% 65%, #FFCA00 0%, #806C00 100%) 0% 0%"
                                    : medal === "silver"
                                      ? "transparent radial-gradient(closest-side at 70% 61%, #B4B4B4 0%, #606060 100%) 0% 0%"
                                      : medal === "bronze"
                                        ? "transparent radial-gradient(closest-side at 67% 64%, #CC8B38 0%, #674019 100%) 0% 0%"
                                        : "inherit"
                                }`,
                              }}
                            >
                              <img src={CrukLogoTransparent} alt="" />
                            </div>
                          ) : null}
                        </div>

                        <div className={styles["name-wrapper"]}>
                          <div
                            className={
                              styles["column-space-around-full-height"]
                            }
                          >
                            <Text
                              className={[
                                sharedStyles["multi-line-ellipsis"],
                                "component-text",
                              ].join(" ")}
                              style={{ WebkitLineClamp: 2 }}
                              marginBottom="none"
                              textAlign="center"
                            >
                              <Link as="span">{`${page.title}`}</Link>
                            </Text>
                          </div>
                        </div>
                        <div className={styles["amount-wrapper"]}>
                          <div
                            className={
                              styles["column-space-around-full-height"]
                            }
                          >
                            <Text>{`£${raisedAmount}`}</Text>
                          </div>
                        </div>
                      </div>
                    </div>
                  </NextLink>
                </li>
              );
            },
          )}
        </ol>
      ) : null}
    </>
  );
};

export default LeaderboardList;
